import type { PrismaClient } from '@prisma/client';
import { Router } from 'express';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler } from '../../core/http.js';
import { authenticate } from '../auth/auth.middleware.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

/** The signed-in user's own tasks and notifications (created by reminders now, CRM later). */
export function createTasksRouter({ db, config }: Deps) {
  const router = Router();
  router.use(['/tasks', '/notifications'], authenticate({ db, config }));

  router.get(
    '/tasks',
    asyncHandler(async (req, res) => {
      const status = req.query.status === 'done' ? 'done' : 'open';
      const rows = await db.task.findMany({
        where: { companyId: req.auth!.companyId, assigneeId: req.auth!.userId, status },
        orderBy: [{ dueDate: 'asc' }, { id: 'desc' }],
        take: 100,
      });
      res.json({
        items: rows.map((t) => ({
          id: t.id,
          title: t.title,
          description: t.description,
          dueDate: t.dueDate?.toISOString().slice(0, 10) ?? null,
          status: t.status,
          relatedType: t.relatedType,
          relatedId: t.relatedId,
          source: t.source,
        })),
      });
    }),
  );

  router.post(
    '/tasks/:id/complete',
    asyncHandler(async (req, res) => {
      const t = await db.task.findFirst({
        where: {
          id: Number(req.params.id),
          companyId: req.auth!.companyId,
          assigneeId: req.auth!.userId,
        },
      });
      if (!t) throw new HttpError(404, 'NOT_FOUND', 'Task not found');
      await db.task.update({
        where: { id: t.id },
        data: { status: 'done', completedAt: new Date() },
      });
      res.status(204).end();
    }),
  );

  router.get(
    '/notifications',
    asyncHandler(async (req, res) => {
      const rows = await db.notification.findMany({
        where: { companyId: req.auth!.companyId, userId: req.auth!.userId },
        orderBy: { id: 'desc' },
        take: 30,
      });
      res.json({ items: rows, unread: rows.filter((n) => !n.readAt).length });
    }),
  );

  router.post(
    '/notifications/read-all',
    asyncHandler(async (req, res) => {
      await db.notification.updateMany({
        where: { companyId: req.auth!.companyId, userId: req.auth!.userId, readAt: null },
        data: { readAt: new Date() },
      });
      res.status(204).end();
    }),
  );

  return router;
}
